"use client";

import Link from "next/link";
import type { JobDoc } from "@/lib/platform/firebase";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import { usePlan } from "@/lib/platform/usePlan";

export const inputClass =
  "mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-rose-400 focus:outline-none dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200";

export function ErrorBox({ message }: { message: string }) {
  return (
    <p className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">{message}</p>
  );
}

// Shown before the user hits the cap. The free try is shared across ALL
// Creator tools (TTS, Speech to Text, Voice Clone, Create Shorts) combined
// — using any one of them uses it up for the rest, which is worth saying
// up front rather than letting people discover it after they're blocked.
export function SharedFreeTrialNote() {
  const { isPro } = usePlan();
  if (isPro) return null;
  return (
    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
      🎁 1 free try shared across all Creator tools — then{" "}
      <Link href="/pricing" className="font-medium text-rose-600 hover:underline dark:text-rose-400">
        Nohaya Pro
      </Link>{" "}
      for unlimited. Pro voices always need a subscription.
    </p>
  );
}

export function JobProgress({ job }: { job: JobDoc | null }) {
  if (!job || job.status === "completed" || job.status === "failed") return null;
  const progress = job.progress ?? 10;
  return (
    <div className="mt-4">
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {job.status === "pending" ? "Queued…" : "Generating…"} {progress}%
      </p>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div className="h-full bg-rose-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

export function AudioResult({ job }: { job: JobDoc | null }) {
  if (!job || job.status !== "completed") return null;
  const audioUrl = (job.result?.audio_url as string) || job.resultUrl;
  if (!audioUrl) return null;

  const locked = job.result?.locked === true;

  return (
    <div className="mt-4 rounded-xl border border-rose-200/80 bg-rose-50/60 p-4 dark:border-rose-800/60 dark:bg-rose-500/5">
      <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">
        {locked ? "Preview" : "Result"}
      </p>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio controls src={audioUrl} className="mt-2 w-full" />
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
        {typeof job.result?.duration_seconds === "number" ? <span>{job.result.duration_seconds.toFixed(1)}s</span> : null}
        {typeof job.result?.word_count === "number" ? <span>{job.result.word_count} words</span> : null}
        {typeof job.result?.quality_improvement_pct === "number" ? (
          <span>{job.result.quality_improvement_pct}% noise reduction</span>
        ) : null}
      </div>
      {locked ? (
        <div className="mt-4 border-t border-rose-200/60 pt-4 dark:border-rose-800/40">
          <p className="mb-3 text-sm text-slate-700 dark:text-slate-300">
            That's a short preview — subscribe to Nohaya Pro to generate the full-length clip.
          </p>
          <UpgradeToPro />
        </div>
      ) : (
        <a href={audioUrl} download className="mt-3 inline-block text-xs font-medium text-rose-600 hover:underline dark:text-rose-400">
          Download audio
        </a>
      )}
    </div>
  );
}
